'use client';

import { wsLogger } from '@/shared/lib/logger';
import { useWebSocketReconnectSync } from './hooks/use-websocket-reconnect-sync';
import {
  useRealtimeSync,
  type DataChangedEvent,
} from './hooks/use-realtime-sync';
import {
  useWebSocketConnection,
  useReconnectSyncHandler,
} from './hooks/useWebSocketConnection';
import { useBroadcastCoordinator } from './hooks/useBroadcastCoordinator';
import { useNetworkRestoreReconnect } from './hooks/useNetworkRestoreReconnect';
import { useTabVisibleReconcile } from './hooks/useTabVisibleReconcile';
import { useWebSocketListeners } from './hooks/useWebSocketListeners';
import { useJobWatchdog } from '@/features/design/job-queue';
import { ErrorBoundary } from '@/shared/ui';

export interface WebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  // Realtime sync hook
  const { handleDataChanged } = useRealtimeSync();

  // Broadcast coordinator for multi-tab support
  const { isLeader, coordinatorRef } = useBroadcastCoordinator({
    handleDataChanged,
    unwrapEventData,
  });

  // WebSocket connection management
  const { isConnected, hasInitiallyConnectedRef } = useWebSocketConnection({
    isLeader,
  });

  // Network-restore nudge (WEB-A.3): `window.online` → forceReconnect if Ably
  // is in a recoverable bad state. Faster than waiting Ably's ~30s ping.
  useNetworkRestoreReconnect({ isLeader });

  // Reconnection sync handler
  const handleReconnectSync = useReconnectSyncHandler(hasInitiallyConnectedRef);
  useWebSocketReconnectSync(handleReconnectSync);

  // L3 reconcile (WEB-L3): tab becomes visible while Ably stayed connected
  // through a brief hide → fire the same sync bundle. Throttled 30s outer
  // + 10s inner (L2). Bad-Ably-state branch is owned by the existing
  // visibility-handler in useWebSocketConnection (forceReconnect → L2).
  useTabVisibleReconcile({ isLeader, onReconnect: handleReconnectSync });

  // L4 watchdog (WEB-L4): 30s tick on leader probes overdue render/upscale
  // jobs via GET /renders/{id}. Last-resort backstop if L1+L2+L3 all miss.
  useJobWatchdog({ isLeader });

  // WebSocket event listeners
  useWebSocketListeners({
    isConnected,
    isLeader,
    handleDataChanged,
    unwrapEventData,
    coordinatorRef,
  });

  // NOTE: We deliberately DON'T disconnect on beforeunload or visibilitychange
  // This allows WebSocket to persist across SPA navigations (Next.js client-side routing)

  return (
    <ErrorBoundary>{children}</ErrorBoundary>
  );
}

/**
 * Unwrap event data from Laravel/Ably format
 * Laravel broadcasts: { data: { entity, action, id, payload, ... } }
 * We need: { entity, action, id, payload, ... }
 */
function unwrapEventData(data: unknown): DataChangedEvent | null {
  if (!data || typeof data !== 'object') {
    wsLogger.warn('[RealtimeSync] Invalid event data:', data);
    return null;
  }

  // Check if data is wrapped
  const wrapped = data as { data?: unknown };
  const eventData = wrapped.data ?? data;

  // Validate required fields
  const event = eventData as Record<string, unknown>;
  if (!event.entity || !event.action || typeof event.ts !== 'number') {
    wsLogger.warn('[RealtimeSync] Missing required fields:', event);
    return null;
  }

  return eventData as DataChangedEvent;
}
